import React, { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Access tiers
export const ACCESS_TIERS = {
  PUBLIC: 'public',       // Tier 1: Read-only pCO2 display
  STANDARD: 'standard',   // Tier 2: Full dashboard access
  ADVANCED: 'advanced'    // Tier 3: Terminal command access
}

// Authentication now handled server-side - no more client-side passwords!

export function AuthProvider({ children }) {
  const [accessTier, setAccessTier] = useState(ACCESS_TIERS.PUBLIC)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [token, setToken] = useState(null)

  // Load saved auth state from localStorage and verify with server
  useEffect(() => {
    const savedToken = localStorage.getItem('buoy-auth-token')
    if (savedToken) {
      verifyToken(savedToken)
    }
  }, [])

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch('/api/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json'
        }
      })
      
      const result = await response.json()
      
      if (result.valid) {
        setToken(tokenToVerify)
        setAccessTier(result.accessTier)
        setIsAuthenticated(result.accessTier !== ACCESS_TIERS.PUBLIC)
        localStorage.setItem('buoy-auth-token', tokenToVerify)
      } else {
        // Token invalid, clear everything
        logout()
      }
    } catch (error) {
      console.error('Token verification failed:', error)
      logout()
    }
  }

  const login = async (password) => {
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      })
      
      const result = await response.json()
      
      if (result.success) {
        setToken(result.token)
        setAccessTier(result.tier)
        setIsAuthenticated(true)
        localStorage.setItem('buoy-auth-token', result.token)
        
        return { success: true, tier: result.tier }
      } else {
        return { success: false, message: result.message }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, message: 'Login failed. Please try again.' }
    }
  }

  const logout = () => {
    setToken(null)
    setAccessTier(ACCESS_TIERS.PUBLIC)
    setIsAuthenticated(false)
    localStorage.removeItem('buoy-auth-token')
  }

  const hasAccess = (requiredTier) => {
    const tierLevels = {
      [ACCESS_TIERS.PUBLIC]: 0,
      [ACCESS_TIERS.STANDARD]: 1,
      [ACCESS_TIERS.ADVANCED]: 2
    }
    return tierLevels[accessTier] >= tierLevels[requiredTier]
  }

  const value = {
    accessTier,
    isAuthenticated,
    token,
    login,
    logout,
    hasAccess,
    ACCESS_TIERS
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}